import React from 'react';
import { useNavigate } from 'react-router-dom';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-lg font-black text-white uppercase tracking-tight mb-4 pb-3 border-b border-white/[0.06]">{title}</h2>
    <div className="space-y-3 text-gray-400 text-sm leading-relaxed">{children}</div>
  </div>
);

const TermsOfService = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <nav className="fixed top-0 w-full z-50 bg-black/70 backdrop-blur-xl border-b border-white/[0.05]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <img
              src="/logo192.png"
              alt="EventDeck"
              className="w-10 h-10 object-contain"
            />
            <h1 className="text-lg font-black tracking-tighter uppercase">EVENT<span className="text-sky-500">DECK</span></h1>
          </div>
          <button onClick={() => navigate(-1)} className="text-[10px] font-black text-gray-500 hover:text-white uppercase tracking-widest transition-colors">← Back</button>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 md:px-6 pt-32 pb-24">
        <div className="mb-12">
          <p className="text-[9px] font-black text-sky-500 uppercase tracking-[0.4em] mb-2">Legal</p>
          <h1 className="text-4xl font-black uppercase tracking-tighter mb-3">Terms of Service</h1>
          <p className="text-gray-500 text-sm">Last updated: April 18, 2026</p>
        </div>

        <Section title="1. Acceptance of Terms">
          <p>By accessing or using EventDeck ("Platform"), you agree to be bound by these Terms of Service. If you do not agree to these terms, you may not use the Platform. These terms apply to all visitors, users, organisers, and participants.</p>
        </Section>

        <Section title="2. Platform Description">
          <p>EventDeck is an event registration and ticketing infrastructure platform. We provide tools for event organisers to manage registrations, payments, and participant access. We act as a technology intermediary — the event organiser is responsible for the event itself.</p>
        </Section>

        <Section title="3. User Accounts">
          <p>To register for events or access certain features, you must create an account. You are responsible for maintaining the confidentiality of your account credentials. You must provide accurate, current, and complete information during registration.</p>
          <p>You are responsible for all activities that occur under your account. Notify us immediately of any unauthorised use at security@eventdeck.app.</p>
        </Section>

        <Section title="4. Event Registration">
          <p>When you register for an event through EventDeck, you enter into a direct agreement with the event organiser. EventDeck facilitates the transaction but is not a party to it.</p>
          <p>Registration is complete only upon successful payment verification by the organiser. A digital QR ticket will be issued to your registered email address upon verification.</p>
          <p>Tickets are non-fungible digital assets tied to the registered participant unless the organiser has explicitly enabled ticket transfers.</p>
        </Section>

        <Section title="5. Payment Terms">
          <p>EventDeck currently supports manual payment verification through mobile banking (bKash, Nagad, Rocket). Participants submit transaction IDs which are verified by organisers.</p>
          <p>By submitting a registration, you confirm that the payment information provided is accurate. Submitting false transaction IDs constitutes fraud and may result in permanent account suspension.</p>
          <p>EventDeck is not responsible for failed or delayed payments due to issues with third-party mobile banking services.</p>
        </Section>

        <Section title="6. Organiser Responsibilities">
          <p>Event organisers are solely responsible for the events they list, including accuracy of event information, delivery of promised services, managing capacity and logistics, and complying with all applicable laws and regulations.</p>
          <p>Organisers must not list fraudulent, illegal, or misleading events. Violations may result in immediate account termination and legal action.</p>
        </Section>

        <Section title="7. Prohibited Conduct">
          <p>You may not use EventDeck to: submit false or misleading information; resell tickets for profit without organiser permission; attempt to circumvent security measures; scrape or harvest data from the platform; impersonate another person or organisation; or engage in any illegal activity.</p>
        </Section>

        <Section title="8. Intellectual Property">
          <p>All content on EventDeck, including logos, design, software, and text, is owned by EventDeck or its licensors and is protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without explicit written permission.</p>
        </Section>

        <Section title="9. Limitation of Liability">
          <p>EventDeck is provided "as is" without warranties of any kind. We are not liable for: event cancellations or changes by organisers; losses arising from incorrect payment information; technical failures or service interruptions; or any indirect, incidental, or consequential damages.</p>
          <p>Our maximum liability to you shall not exceed the amount paid for the specific registration giving rise to the claim.</p>
        </Section>

        <Section title="10. Termination">
          <p>We reserve the right to suspend or terminate your account at any time for violations of these terms. You may terminate your account by contacting us at support@eventdeck.app.</p>
        </Section>

        <Section title="11. Governing Law">
          <p>These terms are governed by the laws of Bangladesh. Any disputes shall be resolved in the courts of Dhaka, Bangladesh.</p>
        </Section>

        <Section title="12. Changes to Terms">
          <p>We reserve the right to modify these terms at any time. Material changes will be communicated via email or prominent notice on the platform. Continued use after changes constitutes acceptance.</p>
        </Section>

        <Section title="13. Contact">
          <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl p-5">
            <p className="text-white font-bold mb-2">EventDeck Legal</p>
            <p>Email: legal@eventdeck.app</p>
            <p>Support: support@eventdeck.app</p>
          </div>
        </Section>
      </main>
    </div>
  );
};

export default TermsOfService;